import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Handshake, Compass, Crown, Flame, Instagram, ArrowRight } from "lucide-react";

const IMAGES = {
  hero: "https://images.pexels.com/photos/33287267/pexels-photo-33287267.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920",
  recechos: "https://images.unsplash.com/photo-1543946207-39bd91e70ca7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxkZWVyJTIwaW4lMjBmb3Jlc3R8ZW58MHx8fHwxNzg2MDk3OTM4fDA&ixlib=rb-4.1.0&q=85",
  internacional: "https://images.unsplash.com/photo-1611504262701-8a48422da4a1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHx3aWxkJTIwYm9hciUyMGZvcmVzdHxlbnwwfHx8fDE3ODYwOTc5NDd8MA&ixlib=rb-4.1.0&q=85",
  sorteos: "https://images.unsplash.com/photo-1694861975760-25a05c59e4e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBuYXR1cmUlMjBsb2RnZXxlbnwwfHx8fDE3ODYwOTc5NDd8MA&ixlib=rb-4.1.0&q=85",
};

const VALUES = [
  { title: "Hermandad", text: "Una comunidad unida por la pasión por el monte y el respeto a la tradición cinegética.", icon: Handshake },
  { title: "Experiencias", text: "Recechos, monterías y viajes internacionales diseñados para vivir la caza al máximo nivel.", icon: Compass },
  { title: "Exclusividad", text: "Fincas privadas, sorteos solo para socios y ofertas que no encontrarás en ningún otro lugar.", icon: Crown },
  { title: "Pasión", text: "Caza responsable, ética y sostenible. Defendemos el campo y sus valores con orgullo.", icon: Flame },
];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end lg:items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Ciervo en el bosque" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-carbon via-transparent to-carbon/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pb-20 lg:pb-0 pt-40 w-full">
          <motion.p {...fadeUp} className="uppercase text-[15px] tracking-[0.25em] text-gold font-body font-semibold mb-5" data-testid="hero-overline">
            Club de Caza Ruta del Agua · Guillena
          </motion.p>
          <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="hero-text-shadow text-5xl sm:text-6xl lg:text-8xl font-light text-white leading-[0.95] tracking-tight max-w-4xl"
            data-testid="hero-title">
            La caza como<br /><span className="text-gold italic font-medium">estilo de vida</span>
          </motion.h1>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="mt-6 text-white/80 text-base lg:text-lg font-light max-w-xl hero-text-shadow" data-testid="hero-subtitle">
            Recechos premium, caza nacional y sorteos exclusivos. Bienvenido a Cinegética Ruta del Agua.
          </motion.p>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.3 }} className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/hazte-socio" className="btn-gold-solid" data-testid="hero-cta-socio">Hazte Socio</Link>
            <Link to="/acciones-de-caza" className="btn-ghost-light" data-testid="hero-cta-saber-mas">Saber Más</Link>
          </motion.div>
        </div>
      </section>

      {/* EXPERIENCES BENTO */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24 lg:py-32" data-testid="experiences-section">
        <motion.div {...fadeUp} className="mb-14 max-w-2xl">
          <p className="overline-gold mb-4">Experiencias</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white font-light tracking-tight">
            Vive la caza que siempre <span className="text-gold italic">soñaste</span>
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          <motion.div {...fadeUp} className="md:col-span-7">
            <Link to="/tienda" className="group relative block h-[380px] lg:h-[520px] overflow-hidden" data-testid="exp-card-recechos">
              <img src={IMAGES.recechos} alt="Recechos Premium" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-black/45 group-hover:bg-black/35 transition-colors duration-500" />
              <div className="absolute bottom-0 left-0 p-8 lg:p-10">
                <p className="overline-gold mb-3">Fincas privadas</p>
                <h3 className="text-3xl lg:text-4xl text-white font-light hero-text-shadow">Monterias Premium</h3>
                <p className="mt-3 text-white/70 text-sm font-light max-w-md">Ciervo, jabalí y corzo en las mejores fincas de España, con guías profesionales.</p>
                <span className="mt-5 inline-flex items-center gap-2 text-gold text-xs uppercase tracking-[0.2em] font-semibold">
                  Descubrir <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </motion.div>
          <div className="md:col-span-5 flex flex-col gap-6 lg:gap-8">
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}>
              <Link to="/acciones-de-caza" className="group relative block h-[240px] lg:h-[246px] overflow-hidden" data-testid="exp-card-internacional">
                <img src={IMAGES.internacional} alt="Aguardos Nocturnos" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-black/50 group-hover:bg-black/40 transition-colors duration-500" />
                <div className="absolute bottom-0 left-0 p-8">
                  <p className="overline-gold mb-2">El jabalí al caer la noche</p>
                  <h3 className="text-2xl lg:text-3xl text-white font-light hero-text-shadow">Aguardos Nocturnos</h3>
                </div>
              </Link>
            </motion.div>
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }}>
              <Link to="/sorteos-exclusivos" className="group relative block h-[240px] lg:h-[246px] overflow-hidden" data-testid="exp-card-sorteos">
                <img src={IMAGES.sorteos} alt="Sorteos Exclusivos" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-black/50 group-hover:bg-black/40 transition-colors duration-500" />
                <div className="absolute bottom-0 left-0 p-8">
                  <p className="overline-gold mb-2">Solo socios</p>
                  <h3 className="text-2xl lg:text-3xl text-white font-light hero-text-shadow">Sorteos Exclusivos</h3>
                </div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-forest border-y border-white/5" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <motion.p {...fadeUp} className="overline-gold mb-14">Nuestros valores</motion.p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            {VALUES.map((v, i) => (
              <motion.div key={v.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                className="border-l border-gold/30 pl-6" data-testid={`value-${v.title.toLowerCase()}`}>
                <v.icon size={26} strokeWidth={1.25} className="text-gold mb-4" />
                <h3 className="text-2xl text-white font-medium">{v.title}</h3>
                <p className="mt-3 text-white/60 text-sm font-light leading-relaxed">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="border-t border-white/5 bg-[#0D0D0D]" data-testid="instagram-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24 text-center">
          <motion.div {...fadeUp}>
            <Instagram size={30} strokeWidth={1.25} className="text-gold mx-auto mb-6" />
            <h2 className="text-3xl lg:text-4xl text-white font-light">
              Síguenos en <span className="text-gold italic">Instagram</span>
            </h2>
            <p className="mt-4 text-white/50 text-sm font-light">@clubdecazacotoprivado — El día a día del club, sorteos y trofeos de nuestros socios.</p>
            <a href="https://facebook.com/clubdecazacotoprivado" target="_blank" rel="noopener noreferrer"
              className="btn-gold mt-8" data-testid="instagram-follow-btn">
              Seguir en Facebook
            </a>
          </motion.div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="relative" data-testid="final-cta-section">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1634843902946-495e95a71703?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxodW50ZXIlMjBzdW5zZXQlMjBmaWVsZHxlbnwwfHx8fDE3ODYwOTgwODh8MA&ixlib=rb-4.1.0&q=85" alt="Atardecer en el campo" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-28 lg:py-36">
          <motion.div {...fadeUp} className="max-w-2xl">
            <p className="overline-gold mb-4">100 € / año</p>
            <h2 className="text-4xl lg:text-6xl text-white font-light leading-tight hero-text-shadow">
              Forma parte del <span className="text-gold italic">club</span>
            </h2>
            <p className="mt-5 text-white/75 font-light hero-text-shadow">
              Sorteos exclusivos, ofertas directas, Video Club y prioridad en todas las experiencias del club.
            </p>
            <Link to="/hazte-socio" className="btn-gold-solid mt-10" data-testid="final-cta-btn">Hazte Socio Ahora</Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
